import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { optional } from 'utils/validations'
import { OdinButton } from '@/components/ui/odin/odin-button'
import { useState } from 'react'
import { showSuccessToast } from 'utils/toast'
import { closeTicket } from 'api/tickets'
import { analytics, ANALYTICS_EVENTS } from 'services/analytics'
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'components/ui/Modal'
import { OdinTextarea } from '@/components/ui/odin/form/odin-textarea'
import { useModal } from 'providers/ModalProvider'

type CloseTicketModalProps = {
  onSuccess?: () => void
  ticketId: string
}

const schema = z.object({
  closingReason: optional,
})

type CloseTicketFormValues = z.infer<typeof schema>

export const CloseTicketModal = ({ onSuccess, ticketId }: CloseTicketModalProps) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const { closeModal } = useModal()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CloseTicketFormValues>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    defaultValues: { closingReason: '' },
  })

  const onSubmit = async (values: CloseTicketFormValues) => {
    try {
      setLoading(true)
      await closeTicket(ticketId, values.closingReason)
      analytics.track(ANALYTICS_EVENTS.TICKET_CLOSED)
      onSuccess?.()
      showSuccessToast(t('tickets.closedSuccess'))
      closeModal()
    } catch (e) {
      console.error({ e })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal onClose={closeModal}>
      <ModalHeader title={t('closeTicket')} icon="circle-check" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody className={'space-y-1'}>
          <OdinTextarea
            label={t('tickets.closingReason')}
            error={errors.closingReason?.message}
            {...register('closingReason')}
          />
        </ModalBody>
        <ModalFooter>
          <OdinButton type={'submit'} className={'w-full'} loading={loading}>
            <span className={'text-sm'}>{t('closeTicket')}</span>
          </OdinButton>
        </ModalFooter>
      </form>
    </Modal>
  )
}
